import { Box, Button, Container, Grid, LinearProgress, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { m } from 'framer-motion';
import Image from 'next/image';
import NextLink from 'next/link';
import PropTypes from 'prop-types';
import { MotionViewport, varFade } from 'src/components/animate';
import useResponsive from 'src/hooks/useResponsive';
import { PATH_PAGE } from 'src/routes/paths';
import { fPercent } from '../../utils/formatNumber';

const RootStyle = styled('div')(({ theme }) => ({
  textAlign: 'center',
  paddingTop: theme.spacing(8),
  backgroundColor: theme.palette.grey[200],
  paddingBottom: theme.spacing(8),
  [theme.breakpoints.up('md')]: {
    textAlign: 'left',
  },
}));

const AboutImage = styled(Image)(() => ({
  width: '100%',
  height: '100%'
}));


const AboutMobileImage = styled(Image)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'block',
  [theme.breakpoints.up('sm')]: {
    display: 'none',
  }
}));

const CustomButton = styled(Button)(({ theme }) => ({
  textAlign: 'center',
  [theme.breakpoints.down('sm')]: {
    textAlign: 'center',
    width: '80%',
    // borderRadius: '3px'
  }
}));


const CustomTypography = styled(Typography)(({ theme }) => ({
  textAlign: 'left',
  [theme.breakpoints.down('sm')]: {
    textAlign: 'center',
    width: '100%',
    fontSize: '22px'
  }
}));

const CustomSubTypography = styled(Typography)(({ theme }) => ({
  textAlign: 'left',
  [theme.breakpoints.down('sm')]: {
    textAlign: 'center',
    width: '100%',
    fontSize: '13px'
  }
}));

// ----------------------------------------------------------------------

export default function AboutWhat() {

  const isDesktop = useResponsive('up', 'md');

  return (
    <RootStyle>
      <Container component={MotionViewport}>
        <Grid container spacing={4}>
          {isDesktop ? (
            <Grid item xs={6}>
              <m.div variants={varFade().inUp}>
                <AboutImage
                  alt="our office 1"
                  src="/images/about/about-us.png"
                  width="500"
                  height="300"
                />
              </m.div>
            </Grid>
          ) :
            (
              <Grid item xs={12}>
                <AboutMobileImage
                  alt="our office 1"
                  src="/images/about/about-us.png"
                  width="200"
                  height="200"
                />
              </Grid>
            )
          }

          <Grid item xs={12} md={6} lg={5}>
            <m.div variants={varFade().inRight}>
              <CustomTypography variant="h2" sx={{ mb: 2 }}>
                {'About us'}
              </CustomTypography>
            </m.div>

            <m.div variants={varFade().inRight}>
              <CustomSubTypography
                sx={{
                  color: (theme) => (theme.palette.mode === 'light' ? 'text.secondary' : 'common.white'),
                }}
              >
                Asset Track for Cloud, LLC (ATC) was conceived and launched as a partnership in 2021. Many of our clients were struggling with tracking assets on Job Sites, data centres, offices, warehouses, and throughout the supply chain process. Businesses were experiencing service delivery and service management delays with their projects and operations.
              </CustomSubTypography>
            </m.div>
            <m.div variants={varFade().inRight}>
              <NextLink href={PATH_PAGE.contact} passHref>
                <CustomButton
                  variant="outlined"
                  color="inherit"
                  size="large"
                  sx={{ mt: 2 }}
                >
                  Contact Us
                </CustomButton>
              </NextLink>
            </m.div>
          </Grid>
        </Grid>
      </Container>
    </RootStyle>

  );
}

// ----------------------------------------------------------------------

ProgressItem.propTypes = {
  progress: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.number,
  }),
};

function ProgressItem({ progress }) {
  const { label, value } = progress;

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center' }}>
        <Typography variant="subtitle2">{label}&nbsp;-&nbsp;</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {fPercent(value)}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={value}
        sx={{
          '& .MuiLinearProgress-bar': { bgcolor: 'grey.700' },
          '&.MuiLinearProgress-determinate': { bgcolor: 'divider' },
        }}
      />
    </Box>
  );
}
